import { createApiClient } from "@space/client";
import {
  createMutation,
  createQuery,
  type MutationOptions,
  type QueryKey,
  type QueryOptions,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/solid-query";
import {
  buildStorageUrl,
  handleApiResponse,
  recordAuthTransportFailure,
  resolveServerUrl,
} from "@space/shared";
// Importing this module registers this build's version/hash/platform with the
// shared telemetry state — see the file header for why initialisation is a side
// effect of the import rather than a call someone has to remember.
import { buildClientInfoHeader } from "@/lib/client-telemetry";
import { readActiveAccountSelector, readActiveImpersonationToken } from "@/lib/accounts";
import { isTauriRuntime } from "@/lib/platform";
import {
  hasTokenPresence,
  LEGACY_TOKEN_ACCOUNT,
  readLegacyToken,
  writeLegacyToken,
} from "@/lib/secure-token";
import { createNativeApiFetch } from "@/lib/native-transport";
import { SERVER_URL } from "@/lib/runtime-config";
import { OBJECT_URL_REVOKE_DELAY_MS } from "@/lib/constants";

/**
 * Persists the legacy session token — **Tauri only**, no-op on web. Writes to
 * the native secure store AND the synchronous in-memory cache, and (Phase 2)
 * refreshes the Rust-held active token so the `api_request` proxy injects it.
 * Kept as `writeSessionToken` for its existing callers (auth.ts, login/register,
 * social-callback).
 */
export function writeSessionToken(token: string | null): void {
  writeLegacyToken(token);
}

/**
 * Bearer token the JS layer attaches to API requests — now ALWAYS `null`.
 *
 * Phase 2 ("native HttpOnly"): on native the token never enters JS for the
 * request path — the Rust `api_request` proxy reads it from the secure store and
 * injects `Authorization: Bearer` itself. On web (cookie-BFF) the token likewise
 * never lives in JS (HttpOnly `space.device` cookie + `X-Active-Account`
 * selector). So JS attaches no Bearer on either platform.
 *
 * Exported for the transport regression tests (native must NOT build a Bearer in
 * JS).
 */
export function getActiveAuthToken(): string | null {
  return null;
}

/**
 * Resolves the secure-store key (userId) whose token Rust should inject on the
 * next API request — **native only**. Prefers THIS tab's active real account and
 * falls back to the legacy single-token slot for back-compat. `null` = guest /
 * unauthenticated → Rust attaches no Bearer. Reads `location.pathname`
 * synchronously (same as the selector), safe to call per request.
 */
function activeTokenAccount(): string | null {
  const selector = readActiveAccountSelector();
  if (selector) {
    return selector;
  }
  // Legacy slot: a cached/plaintext value OR a Rust-held (presence-only) token
  // from the native OAuth deep-link path both count as "authenticated".
  return readLegacyToken() !== null || hasTokenPresence(LEGACY_TOKEN_ACCOUNT)
    ? LEGACY_TOKEN_ACCOUNT
    : null;
}

/**
 * Resolves the active account's userId for the `X-Active-Account` selector
 * header — **web (cookie-BFF) only**.
 *
 *   - Active **real** account → its userId. Combined with the device cookie the
 *     backend resolves THIS tab's session (anti-impersonation: only an account
 *     logged in on this device can be selected).
 *   - Active **guest** account / none → `null` → no header → the device branch
 *     is unauthenticated (clean visitor/local-first, no cross-account leak).
 *   - **Tauri** → `null`: it authenticates directly via Bearer, no selector.
 */
function getActiveAccountSelector(): string | null {
  if (isTauriRuntime()) {
    return null;
  }
  return readActiveAccountSelector();
}

/**
 * F2-C: resolves THIS tab's impersonation JWT for the `X-Impersonation-Token`
 * header, or `null` when not impersonating. Present on both web and Tauri: the
 * "ver como" token is short-lived and lives in JS by design (it is the only web
 * token that does), so the header path applies regardless of platform.
 */
function getImpersonationTokenHeader(): string | null {
  return readActiveImpersonationToken();
}
/**
 * Resolved backend origin for this build (e.g. `http://localhost:3333` in
 * Tauri, or the same origin via the Vite proxy on plain web).
 *
 * Exported so non-openapi-fetch transports (multipart upload, plain `<a>`
 * download links) can target the backend with the **same** origin-resolution
 * logic the typed `api` client uses. Without this, relative URLs would resolve
 * against the WebView origin (`tauri.localhost`) and 404 against the SPA's
 * `index.html`.
 */
export const SERVER_BASE_URL = resolveServerUrl({
  isWeb: true,
  isDev: import.meta.env.DEV,
  serverUrl: SERVER_URL,
  location: {
    hostname: globalThis.window.location.hostname,
    protocol: globalThis.window.location.protocol,
    origin: globalThis.window.location.origin,
  },
});

/**
 * Native (Tauri) API transport: every request is routed through the Rust
 * `api_request` command, which injects `Authorization: Bearer` from the secure
 * store — the token never lives in JS (the "native HttpOnly" model, Phase 2).
 * `null` on web, where the client keeps its same-origin cookie transport.
 */
const nativeApiFetch = isTauriRuntime() ? createNativeApiFetch(activeTokenAccount) : null;

export const api = createApiClient({
  baseUrl: SERVER_BASE_URL,
  // Native: hand the client the Rust-proxy transport (Bearer injected in Rust).
  // Web: leave unset → the client uses its built-in cookie-BFF `buildFetch`.
  fetch: nativeApiFetch ?? undefined,
  // Both platforms attach NO Bearer from JS (always `null`); native's header is
  // added by Rust, web relies on the HttpOnly cookie.
  getAuthToken: getActiveAuthToken,
  // Web attaches the `X-Active-Account` selector; Tauri returns `null` (its
  // active account is carried by the Rust-held token instead).
  getActiveAccountId: getActiveAccountSelector,
  // F2-C: when the active account is an impersonation entry, attach its JWT as
  // `X-Impersonation-Token` — the backend then serves the request AS the target.
  getImpersonationToken: getImpersonationTokenHeader,
  // cookie-BFF: the request carries the HttpOnly `space.device` cookie and the
  // active account is chosen by the `X-Active-Account` header. The token never
  // lives in JS. (On native the custom `fetch` above supersedes this.) OAuth is
  // unaffected (its `state` cookie + callback token ride top-level navigations).
  credentials: "include",
  // Client telemetry (docs/CLIENT_TELEMETRY.md). Attached by an openapi-fetch
  // middleware inside the client, so it survives the native `fetch` override
  // above — which is the whole reason it is not in `buildFetch`.
  getClientInfo: buildClientInfoHeader,
  // A 401 only counts as an anomaly when we believed we had an account. On web
  // that is the `X-Active-Account` selector; on native it is the secure-store
  // key Rust injects the Bearer for. Both are already computed above, so this
  // asks the same questions the transport asks and cannot drift from them.
  isAuthenticatedHint: () =>
    (isTauriRuntime() ? activeTokenAccount() : getActiveAccountSelector()) !== null,
  onAuthTransportFailure: recordAuthTransportFailure,
});

/**
 * Fetch for the raw storage transports (multipart upload, blob download) that
 * bypass the typed `api` client. On native it routes through the Rust proxy so
 * these requests ALSO get the Bearer injected out-of-JS; on web it is the plain
 * `fetch` (the device cookie rides `credentials: "include"`).
 */
function platformFetch(input: string | URL | Request, init?: RequestInit): Promise<Response> {
  if (nativeApiFetch) {
    return nativeApiFetch(input, init);
  }
  return fetch(input, init);
}

/**
 * Builds the auth headers for the raw storage transports (multipart upload, blob
 * download) that bypass the typed `api` client. Mirrors the client's auth: web
 * attaches the `X-Active-Account` selector (the device cookie rides
 * `credentials: "include"` at the call site). Native attaches NOTHING here — its
 * Bearer is injected by the Rust proxy (see {@link platformFetch}), so the token
 * never lives in JS.
 */
function buildStorageAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const activeAccountId = getActiveAccountSelector();
  if (activeAccountId) {
    headers["X-Active-Account"] = activeAccountId;
  }
  const impersonationToken = getImpersonationTokenHeader();
  if (impersonationToken) {
    headers["X-Impersonation-Token"] = impersonationToken;
  }
  return headers;
}

/**
 * F3-B: auth headers for raw JSON/multipart transports that bypass the typed
 * client (gradebook, bulk grades, cohort close, submission upload). Same auth as
 * the storage transports: web attaches `X-Active-Account` (+ impersonation);
 * native's Bearer is injected by the Rust proxy.
 */
export function apiAuthHeaders(): Record<string, string> {
  return buildStorageAuthHeaders();
}

/** F3-B: platform-aware fetch for raw transports (routes through Rust on Tauri). */
export function platformApiFetch(
  input: string | URL | Request,
  init?: RequestInit,
): Promise<Response> {
  return platformFetch(input, init);
}

// ── F3-J: IO kit raw transport (import multipart + xlsx/csv downloads)
// that bypasses the typed client. Same auth as the storage transports: web
// = cookie HttpOnly (`credentials: "include"`) + selector `X-Active-Account`;
// native = Bearer injected by the Rust proxy via `platformFetch`. ─────────────
export async function ioFetchRaw(
  path: string,
  init?: Omit<RequestInit, "headers"> & { headers?: Record<string, string> },
): Promise<Response> {
  const headers: Record<string, string> = {
    ...buildStorageAuthHeaders(),
    ...init?.headers,
  };
  return platformFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
}
// ── end F3-J ──────────────────────────────────────────────────────────────────

/** Shape returned by `POST /api/storage/upload` on both Go and Rust backends. */
interface StorageUploadResult {
  /** Storage key, e.g. `dev/2026/05/30/file.png`. */
  key: string;
  /** Relative or absolute URL to fetch the object back. */
  url: string;
}

/**
 * Uploads a file to the shared object storage (`POST /api/storage/upload`).
 *
 * The endpoint requires a session (it hangs off `protected_routes` and the
 * handler takes `AuthUser`), so we forward the Tauri bearer token and the
 * cookies. We must NOT set `Content-Type` manually — the browser appends the
 * multipart boundary the backend's parser needs.
 */
export async function uploadStorageObject(file: File): Promise<StorageUploadResult> {
  const formData = new FormData();
  formData.append("file", file);

  const headers = buildStorageAuthHeaders();

  const response = await platformFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}/api/storage/upload`, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Storage upload failed with status ${response.status}`);
  }

  // Trust-boundary cast of the server's JSON response; the shape is validated
  // immediately below before any field is used.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const data = (await response.json()) as Partial<StorageUploadResult>;
  if (!data.url) {
    throw new Error("Storage upload response is missing the file URL");
  }
  return { key: data.key ?? "", url: data.url };
}

/** Response from `POST /api/avatar`. */
export interface AvatarUploadResult {
  /** Storage key that must be written to `user.image`. */
  key: string;
  /** Absolute URL ready for an `<img src>`. */
  url: string;
}

/**
 * Uploads the profile photo (`POST /api/avatar`).
 *
 * Only the BINARY travels: the key it returns gets written to the local row via
 * `updateUserImageMutation` and uploads on its own through the sync write-path
 * (same as language, font and theme). So the photo shows up instantly.
 *
 * Careful: `Content-Type` can't be set by hand — the browser has to add
 * the multipart boundary the backend's parser expects.
 */
export async function uploadAvatar(file: File): Promise<AvatarUploadResult> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await platformFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}/api/avatar`, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers: buildStorageAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Avatar upload failed with status ${response.status}`, {
      cause: response.status,
    });
  }

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const data = (await response.json()) as Partial<AvatarUploadResult>;
  if (!data.key) {
    throw new Error("Avatar upload response is missing the storage key");
  }
  return { key: data.key, url: data.url ?? "" };
}

/** Removes the profile photo (`DELETE /api/avatar`). Falls back to the initials. */
export async function deleteAvatar(): Promise<void> {
  const response = await platformFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}/api/avatar`, {
    method: "DELETE",
    credentials: "include",
    headers: buildStorageAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Avatar delete failed with status ${response.status}`, {
      cause: response.status,
    });
  }
}

/**
 * Resolves a stored attachment URL into something this build can open, using
 * the resolved backend origin. Thin wrapper over the shared `buildStorageUrl`
 * so the resolution rules stay identical across web, Tauri and native.
 */
export function resolveStorageUrl(url: string | null | undefined): string {
  return buildStorageUrl(SERVER_BASE_URL, url);
}

type TauriSaveResult = "saved" | "cancelled" | "unavailable";

/**
 * Best-effort native "Save As…" inside Tauri using the dialog + fs plugins.
 *
 * Returns:
 *   - `"saved"`       file written to the user-chosen path
 *   - `"cancelled"`   user dismissed the save dialog (do nothing further)
 *   - `"unavailable"` plugins/scope not usable → caller should fall back
 *
 * The dynamic imports are only reached under `isTauriRuntime()`, so plain web
 * builds never load these chunks. Any failure degrades gracefully to the blob
 * download path; we never block the user on a missing native capability.
 */
async function tryTauriSaveBlob(blob: Blob, filename: string): Promise<TauriSaveResult> {
  try {
    const [{ save }, { writeFile }] = await Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs"),
    ]);
    const path = await save({ defaultPath: filename });
    if (!path) {
      return "cancelled";
    }
    const bytes = new Uint8Array(await blob.arrayBuffer());
    await writeFile(path, bytes);
    return "saved";
  } catch (error) {
    console.warn("[Attachments] native save unavailable, falling back to blob:", error);
    return "unavailable";
  }
}

/** Programmatically triggers a browser download/open for the given href. */
function triggerAnchorDownload(href: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = "noopener noreferrer";
  anchor.target = "_blank";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Downloads a stored attachment and hands it to the user as a file.
 *
 * Why not a plain `<a href>`? The production backend protects `/api/storage/*`
 * behind auth. A top-level link/navigation can't attach the Tauri bearer token
 * (cookies are dropped cross-site at `tauri.localhost`), so it would 401 there.
 * Fetching with the same auth transport the API client uses (cookies on web,
 * bearer in Tauri) works identically on every platform; the resulting `blob:`
 * URL is same-origin so opening/saving it needs no further auth.
 */
export async function downloadStorageObject(
  url: string | null | undefined,
  filename: string,
): Promise<void> {
  const resolved = resolveStorageUrl(url);
  if (!resolved) {
    throw new Error("Attachment has no URL to download");
  }

  // Already-local refs (legacy blob:, inline data:) need no fetch.
  if (/^(?:blob:|data:)/i.test(resolved)) {
    triggerAnchorDownload(resolved, filename);
    return;
  }

  const headers = buildStorageAuthHeaders();

  const response = await platformFetch(resolved, { credentials: "include", headers });
  if (!response.ok) {
    throw new Error(`Attachment download failed with status ${response.status}`);
  }

  const blob = await response.blob();

  // In Tauri the WebView's `<a download>` handling is platform-dependent, so we
  // prefer a real native "Save As…" dialog. If the plugins/scope aren't usable
  // we fall through to the blob anchor below (which still has the fetched bytes).
  if (isTauriRuntime()) {
    const result = await tryTauriSaveBlob(blob, filename);
    if (result === "saved" || result === "cancelled") {
      return;
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    triggerAnchorDownload(objectUrl, filename);
  } finally {
    // Revoke after a delay so the click has consumed the URL first.
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, OBJECT_URL_REVOKE_DELAY_MS);
  }
}

// ─── MEDIA-F3F: media API (VOD) transport ────────────────────────────────────
/**
 * Authenticated `fetch` for the media API (presigned multipart uploads,
 * signed playback, poster). Reuses EXACTLY the same auth resolution
 * as the other raw transports (multipart/blob): on web the device cookie
 * travels with `credentials: "include"` + `X-Active-Account` selector;
 * on Tauri the Rust proxy injects the Bearer outside of JS. `path` is relative to the
 * API (e.g. `/api/media/uploads`). Does NOT set `Content-Type` on multipart (lets
 * the browser set the boundary). Returns the raw `Response` so the
 * caller can read `json()`, headers (ETag) or the 302 playback redirect.
 */
export function mediaApiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = `${SERVER_BASE_URL.replace(/\/$/, "")}${path}`;
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(buildStorageAuthHeaders())) {
    headers.set(k, v);
  }
  return platformFetch(url, { ...init, credentials: "include", headers });
}
// ─── end MEDIA-F3F ───────────────────────────────────────────────────────────

/**
 * F3-A: downloads the user's ICS calendar (`GET /api/me/schedule.ics`) as a
 * file. Uses the same authenticated transport as the rest (cookie on web, Bearer
 * injected in Rust on native) — a plain `<a href>` would 401 in production.
 */
export async function downloadScheduleIcs(): Promise<void> {
  const headers = buildStorageAuthHeaders();
  const response = await platformFetch(
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/me/schedule.ics`,
    {
      credentials: "include",
      headers,
    },
  );
  if (!response.ok) {
    throw new Error(`ICS download failed with status ${response.status}`);
  }
  const blob = await response.blob();
  if (isTauriRuntime()) {
    const result = await tryTauriSaveBlob(blob, "space-academy.ics");
    if (result === "saved" || result === "cancelled") {
      return;
    }
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    triggerAnchorDownload(objectUrl, "space-academy.ics");
  } finally {
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, OBJECT_URL_REVOKE_DELAY_MS);
  }
}

/**
 * Deprecated: Use 'api' instead.
 * Keeping it briefly for compatibility during refactor.
 * @deprecated Use handleApiResponse from @space/shared directly or the query helpers below.
 */
export async function fetchApi<T>(
  promise: Promise<{ data?: T | null; error?: unknown; response?: Response }>,
): Promise<NonNullable<T>> {
  return handleApiResponse(promise);
}

// ─── Query Helpers for SolidJS ───────────────────────────────────────────────

/**
 * Wraps `createQuery`'s result so that **reading `.data` never suspends**.
 *
 * solid-query's `createQuery` is backed by a resource, and on its proxy
 * `data` is the ONLY field that suspends: while the query is fetching with no
 * value yet (initial load, and also every RETRY after an error), reading it throws to
 * the nearest `<Suspense>` boundary. Since that boundary is almost never the one
 * you'd expect —if the read lives in the shell, it's the router's, above
 * everything else— the result is that the screen, or the whole app, goes blank
 * with nothing raising an error.
 *
 * Here `data` returns `undefined` except on success. Each screen then decides
 * what to paint with `isPending` / `isError`, which is what should have happened
 * from the start: loading, error and empty are three different states.
 *
 * It's the same stance `createPowerSyncQuery` already took in the canon, where the
 * default path is signal-based «so a sub-100ms local read never
 * blanks/flashes the tree behind a <Suspense> boundary». That covered the
 * local-first layer; this covers REST.
 *
 * Access still goes through the original proxy (`Reflect.get`), so
 * solid-query's reactivity stays fully intact — only `data` is intercepted.
 * Whoever genuinely needs to suspend (unlikely here: the app is
 * local-first and guest-first) can use `createQuery` directly and own that.
 */
export function nonSuspendingQuery<TQuery extends { isSuccess: boolean }>(query: TQuery): TQuery {
  return new Proxy(query, {
    get(target, prop, receiver) {
      if (prop === "data" && !target.isSuccess) {
        return undefined;
      }
      return Reflect.get(target, prop, receiver) as unknown;
    },
  });
}

/**
 * `createQuery` that doesn't suspend when reading `.data` (see `nonSuspendingQuery`).
 * This is the one screens should use; bare `createQuery` leaves the tree at
 * the mercy of a suspension boundary they don't control.
 *
 * Declared with `createQuery`'s TYPE to inherit its overloads and its
 * inference as-is: typing the generics by hand here narrowed the options'
 * type and broke `data`'s inference on every call.
 */
// The assertion is the price of inheriting the overloads: `createQuery` uses them
// to infer `data` on every call, and there's no way to re-export that type without
// asserting it. It's SAFE because the wrapper doesn't change the result's shape —
// it only intercepts `data` to return `undefined` instead of suspending.
// oxlint-disable-next-line typescript/no-unsafe-type-assertion
export const createSafeQuery = ((...args: Parameters<typeof createQuery>) =>
  nonSuspendingQuery(createQuery(...args))) as typeof createQuery;

export function createApiQuery<TData, TError>(
  queryKey: () => QueryKey,
  queryFn: () => Promise<{ data?: TData | null; error?: TError; response?: Response }>,
  options?: () => Omit<
    QueryOptions<NonNullable<TData>, NonNullable<TError>>,
    "queryKey" | "queryFn" | "initialData"
  >,
): UseQueryResult<NonNullable<TData>, NonNullable<TError>> {
  return nonSuspendingQuery(
    createQuery(() => ({
      ...options?.(),
      queryKey: queryKey(),
      queryFn: async () => handleApiResponse(queryFn()),
    })),
  );
}

export function createApiMutation<TVar, TData, TError, TContext = unknown>(
  mutationFn: (vars: TVar) => Promise<{ data?: TData | null; error?: TError; response?: Response }>,
  options?: () => Omit<
    MutationOptions<NonNullable<TData>, NonNullable<TError>, TVar, TContext>,
    "mutationFn"
  >,
): UseMutationResult<NonNullable<TData>, NonNullable<TError>, TVar, TContext> {
  return createMutation(() => ({
    ...options?.(),
    mutationFn: async (vars) => handleApiResponse(mutationFn(vars)),
  }));
}
